/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
* Goal: Summary of receivables
*/
import { TenantService } from '@src/app/shared/tenant.service';
import { DashboardChart } from '@src/app/shared/dashboard/dashboard-widget/dashboard-chart';


import * as Highcharts from 'highcharts';
import { uuid } from '@src/app/model/abstract-object';
import { NrHelper } from '@src/app/shared/NrHelper';
import { ChosenWidget } from '@src/app/shared/dashboard/DashboardTypes';
import { ApDetailPageNavRoute } from '@src/app/routes/ap-detail-page.nav';
import { PurchasePageNavRoute } from '@src/app/routes/purchase-page-nav';

class PeriodData {
    periodId: uuid;
    name: string;
    totalExpenses: string;
}

class PayablesChartData {
    statusId: uuid;
    cycleId: uuid;
    previousCycleId: uuid;
    cycleName: string;
    totalBudget: number;
    previousPurchases: PeriodData[];
    currentYearPurchases: PeriodData[];
    previousYearExpenses: PeriodData[];
    currentYearExpenses: PeriodData[];
}

export class PayablesChart extends DashboardChart {
    dataUrl = TenantService.ajaxPath + 'omc/dashboard/payables';

    data: PayablesChartData;

    title = $localize `Payables Analysis`;
    subTitle = '';

    options = [];

    constructor(public chosenWidget: ChosenWidget) {
        super(chosenWidget);
        // When I pass navigatePoint into highCharts, this will make it continue to be bound to the current class!
        this.navigatePoint = this.navigatePoint.bind(this);
    }
    resetChart() {
        // Reset any counters for new data
    }

    getSubTitle() {
        return super.getSubTitle(this.subTitle);
    }

    protected setupData(data: PayablesChartData) {
        this.data = data;
    }

    navigatePoint(event: Highcharts.PointClickEventObject) {
        const point = event.point as unknown as {categoryType: string, periodId: uuid};
        if (point.categoryType === 'previousPurchases') {
            this.router.navigate(PurchasePageNavRoute.getListRouterLink({periodId: point.periodId, txnCycleId: this.data.previousCycleId}))
        } else if (point.categoryType === 'currentYearPurchases') {
            this.router.navigate(PurchasePageNavRoute.getListRouterLink({periodId: point.periodId, txnCycleId: this.data.cycleId}))
        } else if (point.categoryType === 'previousYearExpenses') {
            this.router.navigate(ApDetailPageNavRoute.getListRouterLink({periodId: point.periodId, txnCycleId: this.data.previousCycleId}))
        } else {
            this.router.navigate(ApDetailPageNavRoute.getListRouterLink({periodId: point.periodId, txnCycleId: this.data.cycleId}))
        }
    }

    yAxis() {
        return {
            min: 0,
            max: this.data.totalBudget,
            // Use plotLines to show the Total Budget line
            plotLines: [{
                value: this.data.totalBudget,
                color: this.redColor,
                dashStyle: 'Dash',
                width: 1,
                zIndex: 5,
                label: {
                    text: (this.width > 200 ? 'Total Budget: ': '') + NrHelper.toFormattedCurrency(this.data.totalBudget),
                    align: 'left',
                    style: {
                        color: 'black'
                    }
                }
            }]
        }
    }

    xAxis() { 
        return {
            categories: ['Previous', this.data.cycleName, 'Previous', this.data.cycleName],
            labels: {
                y: 30
            },
            plotBands: [{
                from: -0.5,
                to: 1.5,
                color: 'transparent',
                //zIndex: 5, // Bring to front (higher than columns which default to 3)
                label: {
                    text: this.width > 200 ? 'Cash Basis' : 'Cash',
                    align: 'center',
                    verticalAlign: 'bottom',
                    style: {
                        fontSize: '12px',
                    }
                }
            }, {
                from: 1.5,
                to: 3.5,
                color: 'transparent',
                //zIndex: 5, // Bring to front (higher than columns which default to 3)
                label: {
                    text: this.width > 200 ? 'Accrual Basis' : 'Accr',
                    align: 'center',
                    verticalAlign: 'bottom',
                    style: {
                        fontSize: '12px',
                    }
                }
            }]
        }    
    }

    toolTip() {
        return {
            // Remove headerFormat and pointFormat
            // Use a custom formatter to build the tooltip explicitly
            formatter: function () {
                // 'this' refers to the Tooltip context object
                const point = this.point;
                
                // Map the internal categoryType key to a readable name
                let fullCategoryName;
                switch (point.categoryType) {
                    case 'previousPurchases':
                        fullCategoryName = 'Previous Year Purchases';
                        break;
                    case 'currentYearPurchases':
                        fullCategoryName = 'Current Year Purchases';
                        break;
                    case 'previousYearExpenses':
                        fullCategoryName = 'Previous Year Expenses';
                        break;
                    case 'currentYearExpenses':
                        fullCategoryName = 'Current Year Expenses';
                        break;
                    default:
                        fullCategoryName = this.x; // Fallback to the category name ('Previous' or 'Current')
                }
                
                // Construct the full tooltip content:
                let tooltipContent = `<span style="font-size: 10px">${fullCategoryName}</span><br/>`;
                
                // Add the quarter/period name and value
                tooltipContent += `${point.periodName}: <b>${Highcharts.numberFormat(point.y, 0, '.', ',')}</b><br/>`;
                
                // Add the stack total
                tooltipContent += `Total: <b>${Highcharts.numberFormat(point.stackTotal, 0, '.', ',')}</b>`;
                
                return tooltipContent;
            }
        }
    }

    chartSeries() {
        const chartSeries = [];
        const keys = ["previousPurchases", "currentYearPurchases", "previousYearExpenses", "currentYearExpenses"];
        // Find the max number of periods/entries in any array
        const maxPeriods = Math.max(...keys.map(key => this.data[key].length));
    
        // 12 common colors for up to 12 periods/stacks
        const commonColors = [
            '#7cb5ec',  '#e4d354', '#a4c798', '#2b908f', 
            '#434348', '#90ed7d', '#f7a35c', '#8085e9', 
            '#f15c80', '#2b908f', '#f45b5b', '#91e8e1',
            '#d359d3'
        ];


        for (let periodIndex = 0; periodIndex < maxPeriods; periodIndex++) {
            const dataPoints = [];
            keys.forEach( key => {
                const item = this.data[key][periodIndex]; // Get the item at the specific index
                const value = item ? parseFloat(item.totalExpenses) : 0;
                const periodName = item ? item.name : `Period ${periodIndex + 1}`;
                const periodId = item?.periodId;

                dataPoints.push({
                    y: value,
                    // Custom properties for click event
                    periodName: periodName,
                    periodId,  
                    categoryType: key 
                });
            });

            // Each series represents one positional segment (e.g., all 1st quarters)
            chartSeries.push({
                // Name the series based on the positional index (e.g., '1st Period', '2nd Period')
                name: `Period ${periodIndex + 1}`, 
                data: dataPoints,
                type: 'column',
                stack: 'expenses', 
                color: commonColors[periodIndex % commonColors.length], // Consistent color by index
                point: {
                    events: {
                        click: (event) => this.navigatePoint(event),
                    }
                },
                // Period names change from year to year, cannot show as legend
                showInLegend: false 
            });
        }
        return chartSeries;
    }

    protected createChartOptions(): Highcharts.Options {

        return {
            chart: {
                backgroundColor: this.chartBackgroundColor,
                borderRadius: this.chartBorderRadius,
                plotBorderWidth: null,
                type: 'column',
                height: this.height,
                width: this.width
            },
            title: this.getChartTitle(),
            subtitle: this.getSubTitle(),
            credits: { enabled: false },
            xAxis: this.xAxis(),
            yAxis: this.yAxis(),
            tooltip: this.toolTip(),
            plotOptions: {
                column: {
                    stacking: 'normal',
                    borderWidth: 0,
                    pointPadding: 0.1, // default is 0.1 — smaller means tighter spacing
                    groupPadding: 0.01, // default is 0.2 — smaller means less space between groups                        
                    dataLabels: { enabled: false }
                },
                series: {
                    cursor: 'pointer', // Makes all points look clickable
                }
            },
            series: this.chartSeries()
        }  as unknown as Highcharts.Options;
    }
}
